import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { TONE } from '../../../data/constants'
import Chip from '../../../components/Chip/Chip'
import EmptyState from '../../../components/EmptyState/EmptyState'
import ReportTable from '../../../components/ReportTable/ReportTable'
import { useAIRiskReportExportMode } from '../../../components/RenderMode/AIRiskReportRenderModeProvider'
import UnavailableCell from '../../../components/UnavailableCell/UnavailableCell'
import styles from '../DataExposureSection.module.scss'
import applicationAccessStyles from './SharePointSitesTable.module.scss'

const SHARING_CAPABILITY_BY_VALUE = {
  0: { messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.ORGANIZATION_ONLY', tone: TONE.OK },
  disabled: { messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.ORGANIZATION_ONLY', tone: TONE.OK },
  1: { messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.NEW_AND_EXISTING_GUESTS', tone: TONE.DANGER },
  externalusersharingonly: {
    messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.NEW_AND_EXISTING_GUESTS',
    tone: TONE.DANGER,
  },
  2: { messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.ANYONE', tone: TONE.DANGER },
  externaluserandguestsharing: {
    messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.ANYONE',
    tone: TONE.DANGER,
  },
  3: { messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.EXISTING_GUESTS', tone: TONE.WARN },
  existingexternalusersharingonly: {
    messageId: 'AI_RISK_REPORT.SHAREPOINT.SITE_SHARING.EXISTING_GUESTS',
    tone: TONE.WARN,
  },
}

const normalizeSharingCapability = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const getSitePath = (webUrl) => {
  try {
    return new URL(webUrl).pathname || '/'
  } catch {
    return webUrl || '-'
  }
}

const renderOwnershipReference = (ownershipReference) => {
  if (!ownershipReference) return '-'

  const identifier = ownershipReference.email || ownershipReference.displayName || ownershipReference.id
  return (
    <div className={`text-body fw-bold ${applicationAccessStyles.ownershipReference}`} title={identifier}>
      {identifier}
    </div>
  )
}

const getTableFooter = ({ shown, total }) => {
  if (shown === 0 || total <= shown) return null
  return <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.SITES_TABLE_FOOTER" values={{ shown, total }} />
}

const renderApplicationList = (applications) => (
  <ul className={applicationAccessStyles.applicationAccessList}>
    {applications.map((application) => {
      const displayName = application.displayName || application.id
      return (
        <li key={application.id} className={applicationAccessStyles.applicationAccessItem}>
          <span className={applicationAccessStyles.applicationName} title={displayName}>
            {displayName}
          </span>
          <span className={applicationAccessStyles.applicationRoles}>[{application.roles.join(', ')}]</span>
        </li>
      )
    })}
  </ul>
)

const renderExportApplicationList = (applications) => (
  <div className={applicationAccessStyles.exportApplicationAccessList}>
    {applications.map((application) => {
      const displayName = application.displayName || application.id
      return (
        <div key={application.id} className={applicationAccessStyles.exportApplicationAccessItem} title={displayName}>
          <span>{displayName}</span>{' '}
          <span className={applicationAccessStyles.applicationRoles}>[{application.roles.join(', ')}]</span>
        </div>
      )
    })}
  </div>
)

const renderApplicationAccess = ({ row, intl, isExportMode }) => {
  if (row.applicationAccess == null) return <UnavailableCell />
  if (row.applicationAccess.length === 0) {
    return (
      <Chip>
        <FormattedMessage id="AI_RISK_REPORT.SHAREPOINT.APPLICATION_ACCESS.NONE" />
      </Chip>
    )
  }

  const countLabel = intl.formatMessage(
    { id: 'AI_RISK_REPORT.SHAREPOINT.APPLICATION_ACCESS.COUNT' },
    { count: row.applicationAccess.length },
  )
  if (isExportMode) {
    return renderExportApplicationList(row.applicationAccess)
  }

  const popoverId = `ai-risk-site-app-access-${row.key}`
  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      overlay={(props) => (
        <Popover id={popoverId} {...props} className={applicationAccessStyles.applicationAccessPopover}>
          <Popover.Header as="h3">
            <FormattedMessage id="AI_RISK_REPORT.SHAREPOINT.APPLICATION_ACCESS.TITLE" />
          </Popover.Header>
          <Popover.Body>{renderApplicationList(row.applicationAccess)}</Popover.Body>
        </Popover>
      )}
    >
      <button type="button" className={applicationAccessStyles.applicationAccessButton}>
        <Chip tone={TONE.INFO}>{countLabel}</Chip>
      </button>
    </OverlayTrigger>
  )
}

export default function SharePointSitesTable({ sites, total, renderDate, isAssessed }) {
  const intl = useIntl()
  const isExportMode = useAIRiskReportExportMode()
  const rows = React.useMemo(() => sites.map((site) => ({ ...site, key: site.entityId })), [sites])
  const columns = [
    {
      key: 'site',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SITE" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.displayName || row.webUrl}</div>
          <div className={styles.cellSub}>{getSitePath(row.webUrl)}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.webUrl || '').toLowerCase(),
    },
    {
      key: 'ownershipReference',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.OWNERSHIP" />,
      render: (row) => renderOwnershipReference(row.ownershipReference),
      sortable: true,
      sortValue: (row) =>
        (
          row.ownershipReference?.email ||
          row.ownershipReference?.displayName ||
          row.ownershipReference?.id ||
          ''
        ).toLowerCase(),
    },
    {
      key: 'sharing',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SHARING_LEVEL" />,
      render: (row) => {
        const sharing = SHARING_CAPABILITY_BY_VALUE[normalizeSharingCapability(row.sharingCapability)]
        if (!sharing) return <UnavailableCell />
        return (
          <Chip tone={sharing.tone}>
            <FormattedMessage id={sharing.messageId} />
          </Chip>
        )
      },
      sortable: true,
      sortValue: (row) => normalizeSharingCapability(row.sharingCapability),
    },
    {
      key: 'applicationAccess',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.APPLICATION_ACCESS" />,
      render: (row) => renderApplicationAccess({ row, intl, isExportMode }),
      sortable: true,
      sortValue: (row) => row.applicationAccessCount ?? -1,
    },
    {
      key: 'lastContentModifiedAt',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.LAST_CONTENT_CHANGE" />,
      render: (row) => (row.lastContentModifiedAt ? renderDate(row.lastContentModifiedAt) : <UnavailableCell />),
      sortable: true,
      sortValue: (row) => row.lastContentModifiedAt || '',
    },
    {
      key: 'fileCount',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.FILES" />,
      render: (row) => (Number.isFinite(row.fileCount) ? intl.formatNumber(row.fileCount) : <UnavailableCell />),
      sortable: true,
      sortValue: (row) => row.fileCount ?? -1,
    },
  ]

  return (
    <ReportTable
      columns={columns}
      rows={rows}
      footer={getTableFooter({ shown: rows.length, total })}
      empty={
        <EmptyState
          messageId={
            isAssessed ? 'AI_RISK_REPORT.DATA.SHAREPOINT.NO_SITES' : 'AI_RISK_REPORT.DATA.SHAREPOINT.SITES_NOT_ASSESSED'
          }
        />
      }
    />
  )
}

SharePointSitesTable.propTypes = {
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      entityId: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      webUrl: PropTypes.string,
      sharingCapability: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lastContentModifiedAt: PropTypes.string,
      ownershipReference: PropTypes.shape({
        type: PropTypes.oneOf(['group', 'user']).isRequired,
        id: PropTypes.string,
        displayName: PropTypes.string,
        email: PropTypes.string,
      }),
      fileCount: PropTypes.number,
      applicationAccess: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          displayName: PropTypes.string,
          roles: PropTypes.arrayOf(PropTypes.string).isRequired,
        }),
      ),
      applicationAccessCount: PropTypes.number,
    }),
  ).isRequired,
  total: PropTypes.number.isRequired,
  renderDate: PropTypes.func.isRequired,
  isAssessed: PropTypes.bool.isRequired,
}
